import { AvatarName, Footer, SelectLang } from "@/components";
import { adminControllerGetDashboard } from "@/services/admin/admin";
import { LinkOutlined } from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { SettingDrawer } from "@ant-design/pro-components";
import "@ant-design/v5-patch-for-react-19";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history, Link } from "@umijs/max";
import { Agentation } from 'agentation';
import defaultSettings from "../config/defaultSettings";
import { AvatarDropdown } from "./components/RightContent/AvatarDropdown";
import { errorConfig } from "./requestErrorConfig";
import { ResponseState } from "./types/response";
import {
  getHttpStatusFallbackMessage,
  getRequestErrorMessage,
} from "./utils/requestError";

const isDev = process.env.NODE_ENV === "development";
const loginPath = "/user/login";

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const token = sessionStorage.getItem("admin_token");
      if (!token) {
        history.push(loginPath);
        return undefined;
      }

      const response = await fetch("/api/admin/dashboard", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = await response.json();
      if (!response.ok || payload?.state !== "success") {
        throw new Error(payload?.message || "管理员身份校验失败");
      }
      const dashboardData = payload.data;
      return {
        ...dashboardData.currentUser,
        access: "admin",
      };
    } catch (_error) {
      // 清除无效token
      sessionStorage.removeItem("admin_token");
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, "/user/register", "/user/register-result"].includes(
      location.pathname
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [<SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu={false}>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (
        !initialState?.currentUser &&
        !sessionStorage.getItem("admin_token") &&
        location.pathname !== loginPath
      ) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
          {isDev && <Agentation />}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: "/api",
  ...errorConfig,
  requestInterceptors: [
    (config: RequestConfig) => {
      config.headers ??= {};
      config.headers.Authorization = `Bearer ${sessionStorage.getItem(
        "admin_token"
      )}`;
      return config;
    },
  ],
  responseInterceptors: [
    (response: any) => {
      // 检查HTTP状态码
      if (![200, 201].includes(response.status)) {
        const error: any = new Error(
          getRequestErrorMessage(
            { response },
            getHttpStatusFallbackMessage(response.status)
          )
        );
        error.response = response;
        throw error;
      }

      // 检查业务状态
      const { message, state } = response.data ?? {};
      if (state !== ResponseState.SUCCESS) {
        const error: any = new Error(message || "请求失败");
        error.name = "BizError";
        error.info = {
          message: message || "请求失败",
          state,
          data: response.data?.data,
        };
        throw error;
      } else {
        return response.data;
      }
    },
  ],
};
